/*
  Nova chat dock — TEST (branch pdp-chat-dock).

  Persistent fixed-bottom dock on the default product template (chat bar
  + add-to-cart), plus a hidden full-viewport overlay containing the REAL
  chat app — the same <nova-chat-landing> element the ai-chat template
  renders (see snippets/nova-chat-app.liquid). This file does NO chat of
  its own: typing or tapping a chip opens the overlay and hands the
  question to that element, so the visitor gets the full experience
  (scripted topic cards, streaming free-text replies, menu, in-chat
  reader). The app's own Close is intercepted to drop them back onto the
  regular PDP at the exact scroll position they left; the app — and its
  conversation — stays mounted for next time. The dock itself acts like
  a sticky add-to-cart: off-screen until the product form's Add to Cart
  scrolls out of view, then it slides up.

  The app tracks its own funnel (session_started on mount, question_typed
  on submit, ...). The dock only fires beacons for what is uniquely its
  own: chip taps routed into the app, and its buy bar. The buy bar's CTA
  is the chat app's, choreography and all (see the add-to-cart section
  below): it syncs the cart via /cart/update.js, refreshes Broadcast's
  cart count silently (no drawer), and settles into "Checkout".

  Handing questions into the app reaches into NovaChatLanding's
  internals (el.input / submit() / ask()) — our own component, and the
  same interface its own bindings use. Wrapped in try/catch: worst case
  the overlay still opens with the app's input focused.
*/

(function () {
  'use strict';

  if (window.customElements.get('nova-chat-dock')) return;

  var FULL_CLIP = 'inset(0px 0px 0px 0px round 0px)';

  // The dock bar's box expressed as a clip-path on the full-screen
  // overlay, so the overlay can be clipped down to exactly the bar.
  function clipFor(rect) {
    return 'inset(' +
      Math.max(0, rect.top) + 'px ' +
      Math.max(0, window.innerWidth - rect.right) + 'px ' +
      Math.max(0, window.innerHeight - rect.bottom) + 'px ' +
      Math.max(0, rect.left) + 'px round 18px)';
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // How long the flavour-name crossfade runs. Longer than the ~335ms in
  // which Broadcast replaces the buy block, so it is rebuilt and resumed
  // on the new markup rather than being cut off (see watchNameSwap).
  var NAME_SWAP_MS = 520;

  // What the dock's button says, matching the offer on the page: the first
  // jar at 50% off (config.firstOrderPrice, 0 when the visitor can't get it
  // — a signed-in returning customer, or another product), three jars at the
  // flat $99, anything else at unit price. "2nd jar half off" is gone (Alex).
  // TODO(prod): the $99 is still display-only here; checkout charges
  // whatever the Shopify discounts say.
  var THREE_JAR_FLAT = 99;
  function bundlePrice(qty, config) {
    var unit = config.unitPrice || 0;
    var first = config.firstOrderPrice || 0;
    if (qty <= 1) return first > 0 ? first : unit;
    if (config.novaPrime && qty >= 3) return THREE_JAR_FLAT + (qty - 3) * unit;
    return unit * qty;
  }
  function formatMoney(amount) {
    return '$' + (Math.round(amount * 100) / 100).toFixed(amount % 1 === 0 ? 0 : 2);
  }

  /* ---------- CTA choreography helpers (nv-cta-* classes/keyframes from
     nova-chat-landing.css). Shared by the dock's CTA and the product
     form's own Add to Cart button below; ported from nova-chat-landing.js
     playAddedAnimation / showSettledCta / resetCartAnimation. ---------- */

  function ctaClear(cta, fx) {
    cta.classList.remove('is-settling');
    fx.innerHTML = '';
    cta.querySelectorAll('.nv-cta-label, .nv-cta-ring').forEach(function (el) { el.remove(); });
  }

  function ctaPlayAdded(cta, fx, oldLabel, newLabel) {
    fx.innerHTML =
      '<span class="nv-cta-fill"></span>' +
      '<span class="nv-cta-orb"></span>' +
      '<span class="nv-cta-gold"></span>' +
      '<span class="nv-cta-flash"></span>';
    // Old/new labels crossfade outside the overflow-hidden fx layer so
    // the ring glow isn't clipped. Remove/reflow/re-add restarts the
    // is-settling animation on a repeat burst (classList.add is a no-op
    // when the class is already there).
    cta.classList.remove('is-settling');
    void cta.offsetWidth;
    cta.querySelectorAll('.nv-cta-label, .nv-cta-ring').forEach(function (l) { l.remove(); });
    var ring = document.createElement('span');
    ring.className = 'nv-cta-ring';
    cta.appendChild(ring);
    var oldEl = document.createElement('span');
    oldEl.className = 'nv-cta-label nv-cta-label--old';
    oldEl.textContent = oldLabel;
    var newEl = document.createElement('span');
    newEl.className = 'nv-cta-label nv-cta-label--new';
    newEl.textContent = newLabel;
    cta.appendChild(oldEl);
    cta.appendChild(newEl);
    cta.classList.add('is-settling');
  }

  function ctaShowSettled(cta, label) {
    cta.querySelectorAll('.nv-cta-label').forEach(function (l) { l.remove(); });
    var el = document.createElement('span');
    el.className = 'nv-cta-label nv-cta-label--settled';
    el.textContent = label;
    cta.appendChild(el);
  }

  function NovaChatDock() {
    return Reflect.construct(HTMLElement, [], NovaChatDock);
  }
  NovaChatDock.prototype = Object.create(HTMLElement.prototype);
  NovaChatDock.prototype.constructor = NovaChatDock;
  Object.setPrototypeOf(NovaChatDock, HTMLElement);

  NovaChatDock.prototype.connectedCallback = function () {
    if (this._connected) return;
    this._connected = true;

    var configEl = this.querySelector('[data-ncd-config]');
    this.config = configEl ? JSON.parse(configEl.textContent) : { variants: [] };
    this.sessionId = this.getSessionId();

    this.el = {
      overlay: this.querySelector('[data-ncd-overlay]'),
      appUrl: this.getAttribute('data-ncd-app-url'),
      appLoading: this.querySelector('[data-ncd-app-loading]'),
      app: null, // fetched and stamped on first reach, see loadApp()
      bar: this.querySelector('[data-ncd-bar]'),
      chips: this.querySelector('[data-ncd-chips]'),
      input: this.querySelector('[data-ncd-input]'),
      send: this.querySelector('[data-ncd-send]'),
      flavor: this.querySelector('[data-ncd-flavor]'),
      flavorName: this.querySelector('[data-ncd-flavor-name]'),
      jarFlavorRow: this.querySelector('[data-ncd-jar-flavors]'),
      jarEditorOverlay: this.querySelector('[data-ncd-jar-editor]'),
      jarEditorList: this.querySelector('[data-ncd-jar-editor-list]'),
      jarEditorDone: this.querySelector('[data-ncd-jar-editor-done]'),
      qtyDec: this.querySelector('[data-ncd-qty-dec]'),
      qtyInc: this.querySelector('[data-ncd-qty-inc]'),
      qtyValue: this.querySelector('[data-ncd-qty-value]'),
      cta: this.querySelector('[data-ncd-cta]'),
      ctaFx: this.querySelector('[data-ncd-cta-fx]'),
      ctaSizer: this.querySelector('[data-ncd-cta-sizer]'),
      ctaFlag: this.querySelector('[data-ncd-cta-flag]'),
      upsell: this.querySelector('[data-ncd-upsell]'),
      upsellBump3: this.querySelector('[data-ncd-upsell-bump3]'),
      upsellClose: this.querySelector('[data-ncd-upsell-close]')
    };

    this.state = {
      qty: 1,
      // One entry per jar (index into config.variants), not one flavor for
      // the whole order — each jar in a multi-jar order carries its own.
      // Kept the same length as qty by syncJarFlavors(). Same model as
      // the app.
      jarFlavors: [Math.max(0, this.config.variants.findIndex(function (v) {
        return v.id === this.config.selectedVariantId;
      }, this))],
      // The product form's own jars, kept apart from the dock's: the page's
      // offer cards and "Edit jars" drive these, the dock's stepper drives
      // jarFlavors above. One shared list meant picking 3 jars on the page
      // silently changed what the sticky bar would add.
      pageJars: [Math.max(0, this.config.variants.findIndex(function (v) {
        return v.id === this.config.selectedVariantId;
      }, this))],
      // Snapshot of jarFlavors as of the last successful cart sync, or
      // null — lets syncCart() diff instead of re-adding everything.
      cartJarFlavors: null,
      committing: false,
      // Set once the add burst has finished and the button reads
      // "Checkout" (second click goes to checkout). Cleared by any qty /
      // flavor change, which returns the button to "Add to Cart".
      revealed: false,
      upsellSeen: false,
      savedScrollY: 0
    };

    // Every cart write from this page (dock CTA, product form CTA, either
    // upsell) takes the next number. A response only gets to record what is
    // in the cart if its number is still the latest, so a slow write can
    // never describe the cart as an older selection that a newer write has
    // already replaced.
    this.cartWriteSeq = 0;

    // The page's cards can come back checked at 3 after a back/forward
    // visit; the dock always starts at one jar of its own.
    this.syncPageJars();

    this.bindEvents();
    this.renderBuyBar();
    this.observeHeight();
    this.observeStickyTrigger();
    this.mainCta = new NovaMainCta(this);
  };

  NovaChatDock.prototype.disconnectedCallback = function () {
    if (this._resizeObserver) this._resizeObserver.disconnect();
    if (this._stickyObserver) this._stickyObserver.disconnect();
    window.removeEventListener('scroll', this._onScrollFallback);
    this.releaseDrawerGuard();
    document.body.style.paddingBottom = '';
    this.unlockScroll();
  };

  // Same key as nova-chat-landing.js on purpose: the app in the overlay
  // reads it too, so PDP and ai-chat template visits are one session.
  NovaChatDock.prototype.getSessionId = function () {
    var KEY = 'nv-chat-session-id';
    try {
      var existing = window.sessionStorage.getItem(KEY);
      if (existing) return existing;
      var id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : (Date.now() + '-' + Math.random().toString(16).slice(2));
      window.sessionStorage.setItem(KEY, id);
      return id;
    } catch (e) {
      return Date.now() + '-' + Math.random().toString(16).slice(2);
    }
  };

  /* ---------- events ---------- */

  NovaChatDock.prototype.bindEvents = function () {
    var self = this;

    this.el.send.addEventListener('click', function () { self.submit(); });
    this.el.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        self.submit();
      }
    });
    this.el.input.addEventListener('input', function () { self.autoGrowInput(); });
    // Reaching for the chat (the chips, the input, send) starts fetching the
    // app, so it is usually in by the time the tap or the typed question
    // opens it. Not the buy row: adding to cart never needs it.
    var warm = function (e) {
      if (!e.target.closest || !e.target.closest('[data-ncd-chips], .ncd-input-row')) return;
      self.loadApp().catch(function () { /* the open will retry */ });
    };
    this.el.bar.addEventListener('pointerdown', warm, { passive: true });
    this.el.bar.addEventListener('focusin', warm);

    this.el.chips.addEventListener('click', function (e) {
      var chip = e.target.closest ? e.target.closest('[data-ncd-topic]') : null;
      if (!chip) return;
      self.track('chip_clicked', chip.textContent.trim());
      self.openChat(null, chip.getAttribute('data-ncd-topic'));
    });

    // The app's own header Close (data-nv-close-chat) normally collapses
    // the thread back to the app's hero. Inside this overlay that would
    // strand the visitor in the chat template, so the click is caught in
    // the CAPTURE phase on the overlay — before it reaches the button's
    // own listener — and closes the overlay instead. The ai-chat template
    // is untouched: nothing there goes through this overlay.
    this.el.overlay.addEventListener('click', function (e) {
      var close = e.target.closest ? e.target.closest('[data-nv-close-chat]') : null;
      if (!close) return;
      e.stopPropagation();
      e.preventDefault();
      self.closeChat();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.el.overlay.hidden) self.closeChat();
    });

    // Single toggle (qty === 1): cycles jar 1's flavor in place.
    this.el.flavor.addEventListener('click', function () {
      var variants = self.config.variants;
      if (variants.length < 2) return;
      self.state.jarFlavors[0] = (self.state.jarFlavors[0] + 1) % variants.length;
      self.resetCartAnimation();
      self.track('flavor_selected', self.currentVariant().title);
    });

    this.el.jarEditorDone.addEventListener('click', function () { self.closeJarEditor(); });
    this.el.jarEditorOverlay.addEventListener('click', function (e) {
      if (e.target === self.el.jarEditorOverlay) self.closeJarEditor();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.el.jarEditorOverlay.classList.contains('is-open')) self.closeJarEditor();
    });

    if (this.el.qtyDec) this.el.qtyDec.addEventListener('click', function () { self.setQty(self.state.qty - 1); });
    if (this.el.qtyInc) this.el.qtyInc.addEventListener('click', function () { self.setQty(self.state.qty + 1); });

    // The product form's offer cards: the dock owns their per-jar flavors
    // (it has the editor), but not their quantity — its own bar stays on
    // whatever the stepper says.
    document.addEventListener('change', function (e) {
      var card = e.target;
      if (!card || !card.classList || !card.classList.contains('nfo-tier__input')) return;
      self.syncPageJars(parseInt(card.value, 10));
      if (self.mainCta && self.mainCta.button) self.mainCta.reset();
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-nfo-jars-edit]')) self.openJarEditor('page');
    });

    this.el.cta.addEventListener('click', function () { self.addToCart(); });

    // Taking the upsell = "yes, add the extra jars too": commits straight
    // to the cart and lands on Checkout, same as the app. qty is set
    // directly (not via setQty) so the button isn't reset to "Add to Cart"
    // first.
    this.el.upsellBump3.addEventListener('click', function () {
      self.track('upsell_choice', '3');
      self.state.qty = 3;
      self.syncJarFlavors();
      self.hideUpsell(true);
      self.renderBuyBar();
      self.commitToCart();
    });
    this.el.upsellClose.addEventListener('click', function () {
      self.track('upsell_choice', 'dismissed');
      self.hideUpsell(true);
    });
  };

  NovaChatDock.prototype.autoGrowInput = function () {
    var input = this.el.input;
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 84) + 'px';
  };

  // Keep page content clear of the fixed dock. Set via JS, not a CSS
  // :has() rule — :has() has previously kept this theme's CSS off
  // Shopify's CDN (see nova-chat-landing.css).
  NovaChatDock.prototype.observeHeight = function () {
    var self = this;
    var update = function () {
      var h = self.el.bar.offsetHeight;
      document.body.style.paddingBottom = h + 24 + 'px';
    };
    this._resizeObserver = new ResizeObserver(update);
    this._resizeObserver.observe(this.el.bar);
    update();
  };

  // Sticky-add-to-cart behavior: the dock stays off-screen until the
  // product form's own Add to Cart has scrolled up out of view, then
  // slides in; it slides back out when that button is on screen again or
  // hasn't been reached yet. Falls back to a plain scroll threshold if
  // the button isn't on the page.
  //
  // On phones the dock also shares the page with the social overlay
  // (nova-social.js), and arrives with its right rail rather than after it:
  // the overlay announces the rail's entrance as a `nova-social:rail` event
  // and the dock shows from that moment. The sticky rule above still
  // applies on its own, so the dock never depends on that script having
  // run, and is the only rule off phones, where there is no overlay.
  NovaChatDock.prototype.observeStickyTrigger = function () {
    var self = this;
    var phone = window.matchMedia('(max-width: 749px)');
    var pastTrigger = false;
    var railIn = false;
    var apply = function () {
      self.classList.toggle('ncd--visible', pastTrigger || (phone.matches && railIn));
    };
    document.addEventListener('nova-social:rail', function (e) {
      railIn = e.detail.entering;
      apply();
    });
    if (phone.addEventListener) phone.addEventListener('change', apply);

    var trigger = document.querySelector('.product__page [data-add-to-cart]');
    if (trigger && 'IntersectionObserver' in window) {
      this._stickyObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      }, { threshold: 0 });
      this._stickyObserver.observe(trigger);
      return;
    }
    // Every page without a product form (the homepage, the blog, the
    // rest): a short scroll brings the dock in (Alex, 2026-09-22: "scroll
    // just a bit"), the way the product page's does once its Add to Cart
    // has passed.
    this._onScrollFallback = function () {
      pastTrigger = window.scrollY > 120;
      apply();
    };
    window.addEventListener('scroll', this._onScrollFallback, { passive: true });
    this._onScrollFallback();
  };

  // position: fixed on body is the lock that actually holds on iOS
  // Safari (overflow: hidden alone doesn't); the negative top keeps the
  // page visually where it was, and unlockScroll() restores the exact
  // scroll position so Close lands the visitor precisely where they left.
  NovaChatDock.prototype.lockScroll = function () {
    this.state.savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -this.state.savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  };

  NovaChatDock.prototype.unlockScroll = function () {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this.state.savedScrollY);
  };

  /* ---------- overlay open/close + handoff into the app ---------- */

  NovaChatDock.prototype.submit = function () {
    var text = (this.el.input.value || '').trim();
    if (!text) return;
    this.el.input.value = '';
    this.autoGrowInput();
    this.openChat(text, null);
  };

  // The chat app is not in the page. Its markup is the product's chat-dock
  // view (templates/product.chat-dock.liquid), fetched the first time the
  // visitor reaches for the chat: a touch or focus on the bar's chat half
  // warms it, and an open waits for it. Until then the page carries none
  // of it (about 56 KB of HTML and the app's script, 2026-09-22). One
  // request per page; a failure clears it so the next reach retries.
  var APP_TIMEOUT_MS = 10000;
  NovaChatDock.prototype.loadApp = function () {
    var self = this;
    if (this._appLoad) return this._appLoad;
    var variant = this.currentVariant();
    var url = this.el.appUrl + (variant.id ? '&variant=' + variant.id : '');
    var abort = 'AbortController' in window ? new AbortController() : null;
    var timer = abort && setTimeout(function () { abort.abort(); }, APP_TIMEOUT_MS);
    this._appLoad = fetch(url, { credentials: 'same-origin', signal: abort ? abort.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('chat app HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        clearTimeout(timer);
        self.stampApp(html);
      })
      .catch(function (err) {
        clearTimeout(timer);
        self._appLoad = null;
        throw err;
      });
    return this._appLoad;
  };

  // Puts the fetched app into the overlay, once.
  NovaChatDock.prototype.stampApp = function (html) {
    if (this.el.app) return;
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var frag = document.importNode(tpl.content, true);

    // Scripts parsed from a string never run, so each script the page does
    // not already have is re-created after the markup is in, in order:
    // nova-chat-landing.js always (it defines the app), nova-3d-modal.js
    // off the product page (its gallery loads it there; without it the
    // chat's 3D hero sits frozen). A second copy of either would throw on
    // its customElements.define.
    var scripts = [];
    frag.querySelectorAll('script[src]').forEach(function (s) {
      var src = s.getAttribute('src');
      var already = Array.prototype.some.call(document.scripts, function (d) { return d.getAttribute('src') === src; });
      if (!already) scripts.push(src);
      s.remove();
    });

    if (this.el.appLoading) this.el.appLoading.remove();
    this.el.overlay.appendChild(frag);
    this.el.app = this.el.overlay.querySelector('nova-chat-landing');
    scripts.forEach(function (src) {
      var script = document.createElement('script');
      script.src = src;
      script.async = false; // run in the order they came
      document.body.appendChild(script);
    });

    // nova-3d-modal.js binds the carousels on the page when it loads; this
    // stamped one wasn't there yet. Same binder, so the arrows behave (and
    // loop) identically. The binder ships with the hero snippet, so a
    // product without the hero has neither it nor a carousel to bind.
    if (window.nova3dBindCarousel) {
      this.el.overlay.querySelectorAll('[data-nova3d-carousel]').forEach(window.nova3dBindCarousel);
    }
  };

  // Open the full-screen chat app; hand it a typed question (text) or a
  // scripted topic id (topicId), or neither (just open).
  NovaChatDock.prototype.openChat = function (text, topicId) {
    var self = this;
    var arrived = !!this.el.app;
    this.lockScroll();
    // Measured before the class lands: adding it starts the bar's slide
    // out, and the expand has to grow from where the bar actually is.
    var from = this.barRect();
    this.el.overlay.hidden = false;
    this.classList.add('ncd--chat-open'); // out-stack the sticky header group
    this.playOverlayExpand(from);

    // The app may still be on its way (loadApp): the overlay shows its
    // loading dots until it lands, then the app fades up. If it cannot be
    // fetched, the overlay closes and a typed question goes back into the
    // bar's input, so nothing the visitor wrote is lost.
    this.loadApp()
      .then(function () {
        if (!arrived && self.el.app && self.el.app.animate && !prefersReducedMotion()) {
          self.el.app.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
        }
        // whenDefined: the markup upgrades to a live NovaChatLanding only
        // once its script has run. Then a frame, so the app's
        // ResizeObservers (header/dock height vars) measure a real layout
        // before the question lands and the thread scrolls.
        return window.customElements.whenDefined('nova-chat-landing');
      })
      .then(function () {
        // Closed while it loaded (Escape): do not send anything.
        if (!self.classList.contains('ncd--chat-open')) return;
        handOver();
      })
      .catch(function () {
        if (text) {
          self.el.input.value = text;
          self.autoGrowInput();
        }
        if (self.classList.contains('ncd--chat-open')) self.closeChat();
      });

    function handOver() {
      requestAnimationFrame(function () {
        var app = self.el.app;
        try {
          // The app was stamped into an overlay that was still display:
          // none, so its input measured 0 tall. Re-measure now that it
          // has a real layout, or the chat box sits there with no
          // placeholder showing.
          if (app && typeof app.autoGrowInput === 'function') app.autoGrowInput();
          if (topicId && app && typeof app.ask === 'function') {
            app.ask(topicId);
          } else if (text && app && app.el && app.el.input && typeof app.submit === 'function') {
            app.el.input.value = text;
            app.submit();
          } else if (app && app.el && app.el.input) {
            app.el.input.focus();
          }
        } catch (e) {
          // App internals unavailable — the overlay is still open and the
          // app is still usable by hand.
        }
      });
    }
  };

  NovaChatDock.prototype.closeChat = function () {
    var self = this;
    var to = this.barRect();
    var finish = function () {
      self.el.overlay.hidden = true;
      self.classList.remove('ncd--chat-closing');
      self.unlockScroll();
    };
    // The open class goes now so the bar slides back up behind the
    // shrinking overlay; a separate class holds the raised z-index until
    // the animation is done, or the overlay would drop under the sticky
    // header mid-close.
    this.classList.remove('ncd--chat-open');
    if (!to || !this.el.overlay.animate || prefersReducedMotion()) { finish(); return; }
    this.classList.add('ncd--chat-closing');
    var anim = this.el.overlay.animate(
      [{ clipPath: FULL_CLIP }, { clipPath: clipFor(to), opacity: 0.55 }],
      { duration: 300, easing: 'cubic-bezier(0.3, 0, 1, 1)' }
    );
    anim.onfinish = finish;
    anim.oncancel = finish;
  };

  // The overlay grows out of the dock bar rather than appearing whole:
  // a clip-path from the bar's own box — rounded corners and all — out
  // to the full viewport, with the app fading up behind it. Exits are
  // shorter than entrances, so the close runs at 300ms against 460ms.
  NovaChatDock.prototype.playOverlayExpand = function (from) {
    if (!from || !this.el.overlay.animate || prefersReducedMotion()) return;
    this.el.overlay.animate(
      [{ clipPath: clipFor(from), opacity: 0.6 }, { clipPath: FULL_CLIP, opacity: 1 }],
      { duration: 460, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    var app = this.el.app;
    if (app && app.animate) {
      app.animate(
        [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
        { duration: 380, delay: 90, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'backwards' }
      );
    }
  };

  // The bar's box in viewport coordinates, or null when it is parked
  // off-screen (nothing to grow from).
  NovaChatDock.prototype.barRect = function () {
    var b = this.el.bar.getBoundingClientRect();
    if (!b.width || b.top >= window.innerHeight) return null;
    return b;
  };

  /* ---------- buy bar ---------- */

  NovaChatDock.prototype.setQty = function (qty) {
    var next = Math.min(6, Math.max(1, qty)); // same cap as the app
    if (next === this.state.qty) return;
    this.state.qty = next;
    this.syncJarFlavors();
    this.resetCartAnimation(); // repaints the number, among the rest
    this.popQty();
    this.track('qty_changed', String(this.state.qty));
  };

  // The app's popQty: the new number scales up into place instead of
  // swapping silently. Reading offsetWidth between remove and add is
  // what restarts the keyframes on a repeat tap.
  NovaChatDock.prototype.popQty = function () {
    var el = this.el.qtyValue;
    if (!el) return;
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
  };

  /* ---------- per-jar flavors (ported from nova-chat-landing.js) ---------- */

  // "Primary" variant — jar 1's flavor: the single toggle shown at
  // qty === 1.
  NovaChatDock.prototype.currentVariant = function () {
    return this.config.variants[this.state.jarFlavors[0]] || this.config.variants[0] || {};
  };

  // Keeps jarFlavors the same length as qty — growing duplicates the last
  // jar's flavor ("one more of what I've got"), shrinking drops the
  // most-recently-added jar first.
  NovaChatDock.prototype.syncJarFlavors = function () {
    var arr = this.state.jarFlavors;
    while (arr.length < this.state.qty) arr.push(arr[arr.length - 1]);
    while (arr.length > this.state.qty) arr.pop();
  };

  var SWAP_ICON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3L4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4"/></svg>';

  // Builds a flavor chip (dot + name + swap icon). The app's version uses
  // an SVG <use> symbol that only exists once the app is stamped; this
  // inlines the icon so the chip works before anyone has opened the chat.
  function flavorChip(className, text, ariaLabel, noDot) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.setAttribute('aria-label', ariaLabel);
    if (!noDot) {
      var dot = document.createElement('span');
      dot.className = className === 'ncd-flavor' ? 'ncd-flavor-dot' : 'nv-flavor-dot';
      btn.appendChild(dot);
    }
    var name = document.createElement('span');
    name.className = className === 'ncd-flavor' ? 'ncd-flavor-name' : 'nv-flavor-name';
    name.textContent = text;
    btn.appendChild(name);
    btn.insertAdjacentHTML('beforeend', SWAP_ICON);
    return btn;
  }

  // qty > 1: one short summary chip ("Edit Jars", full breakdown in the
  // aria-label) in the toggle's slot — there's no room for a readable
  // chip per jar next to the stepper and CTA. Tapping opens the editor.
  NovaChatDock.prototype.renderJarFlavorRow = function () {
    var self = this;
    this.el.jarFlavorRow.innerHTML = '';
    var titles = this.state.jarFlavors.map(function (i) { return (self.config.variants[i] || {}).title || ''; });
    var unique = titles.filter(function (t, i) { return titles.indexOf(t) === i; });
    var detail = unique.length === 1
      ? this.state.qty + ' jars, all ' + unique[0]
      : this.state.qty + ' jars, mixed flavors';
    // No flavor dot on the summary: it means nothing for a mixed order and
    // its 14px is what "Edit Jars" needs to fit on a 360px phone.
    var btn = flavorChip('ncd-flavor ncd-flavor--summary', 'Edit Jars', 'Edit flavor per jar — currently ' + detail, true);
    btn.addEventListener('click', function () { self.openJarEditor('dock'); });
    this.el.jarFlavorRow.appendChild(btn);
  };

  // Which list the editor is editing: 'dock' (the bar's own stepper order)
  // or 'page' (the product form's offer cards). Same panel, two orders.
  NovaChatDock.prototype.editorJars = function () {
    return this.editorTarget === 'page' ? this.state.pageJars : this.state.jarFlavors;
  };

  NovaChatDock.prototype.renderJarEditorList = function () {
    var self = this;
    this.el.jarEditorList.innerHTML = '';
    this.editorJars().forEach(function (variantIdx, jarIdx) {
      var v = self.config.variants[variantIdx] || {};
      var row = document.createElement('div');
      row.className = 'nv-jar-editor-row';
      var label = document.createElement('span');
      label.className = 'nv-jar-editor-row-label';
      label.textContent = 'Jar ' + (jarIdx + 1);
      row.appendChild(label);
      var btn = flavorChip('nv-flavor-toggle', v.title || '', 'Change flavor for jar ' + (jarIdx + 1));
      btn.addEventListener('click', function () {
        if (self.config.variants.length < 2) return;
        var jars = self.editorJars();
        jars[jarIdx] = (jars[jarIdx] + 1) % self.config.variants.length;
        if (self.editorTarget === 'page') {
          self.renderPageJars();
          if (self.mainCta && self.mainCta.button) self.mainCta.reset();
        } else {
          self.resetCartAnimation();
        }
        self.renderJarEditorList();
      });
      row.appendChild(btn);
      self.el.jarEditorList.appendChild(row);
    });
  };

  NovaChatDock.prototype.openJarEditor = function (target) {
    this.editorTarget = target === 'page' ? 'page' : 'dock';
    this.track('jar_editor_opened', this.editorTarget + ' x' + this.editorJars().length);
    this.renderJarEditorList();
    this.el.jarEditorOverlay.classList.add('is-open');
    this.classList.add('ncd--editor-open');
  };

  NovaChatDock.prototype.closeJarEditor = function () {
    this.el.jarEditorOverlay.classList.remove('is-open');
    this.classList.remove('ncd--editor-open');
  };

  // The per-jar flavor line under the page's 3 jar card: "3 jars, all Pina
  // Colada" or "2 Pina Colada, 1 Mango Cream", shown only while more than
  // one jar is picked. Mixed flavors also flag the product form, which
  // hides Shop Pay (it can only buy one variant; see
  // product-offer-tiers.liquid).
  // Grows or shrinks the product form's jar list to the card it has checked
  // (1 or 3), then repaints the row under the cards. Growing duplicates the
  // last jar's flavor, same rule the dock's own stepper uses.
  NovaChatDock.prototype.syncPageJars = function (qty) {
    var arr = this.state.pageJars;
    var checked = document.querySelector('[data-nfo-tiers] .nfo-tier__input:checked');
    var want = qty || (checked ? parseInt(checked.value, 10) : arr.length) || 1;
    while (arr.length < want) arr.push(arr[arr.length - 1]);
    while (arr.length > want) arr.pop();
    this.renderPageJars();
  };

  NovaChatDock.prototype.renderPageJars = function () {
    var row = document.querySelector('[data-nfo-jars]');
    if (!row) return;
    var multiJar = this.state.pageJars.length > 1;
    var variants = this.config.variants;
    var names = [];
    var counts = {};
    this.state.pageJars.forEach(function (idx) {
      var title = (variants[idx] || {}).title || '';
      if (!counts[title]) { counts[title] = 0; names.push(title); }
      counts[title] += 1;
    });
    var mixed = multiJar && names.length > 1;
    row.hidden = !multiJar;
    var summary = row.querySelector('[data-nfo-jars-summary]');
    if (summary) {
      summary.textContent = mixed
        ? names.map(function (n) { return counts[n] + ' ' + n; }).join(', ')
        : this.state.pageJars.length + ' jars, all ' + (names[0] || '');
    }
    // Shop Pay buys the form's ONE hidden variant at the form's quantity, so
    // it is only safe while that variant is what the jars actually say. Mixed
    // jars are the obvious case; the subtler one is "Edit jars" putting every
    // jar on a flavor the page's picker is not on (the editor deliberately
    // does not drive Broadcast's picker), which leaves the jars uniform while
    // the form still points at the old variant. Both hide it.
    var form = this.mainCta && this.mainCta.form;
    if (!form) return;
    var formIdInput = form.querySelector('[name="id"]');
    var formId = formIdInput ? formIdInput.value : null;
    var jarVariant = variants[this.state.pageJars[0]] || {};
    var offForm = multiJar && (mixed || String(jarVariant.id) !== String(formId));
    form.classList.toggle('nfo-jars-mixed', offForm);
  };

  // Picking a flavor on the page sets every jar to it; Edit jars can mix
  // them after. Keeps the dock's jar 1 on the page's flavor too.
  NovaChatDock.prototype.setAllJarsToVariant = function (variantId) {
    var idx = this.config.variants.findIndex(function (v) { return String(v.id) === String(variantId); });
    if (idx < 0) return;
    this.state.pageJars = this.state.pageJars.map(function () { return idx; });
    this.state.jarFlavors = this.state.jarFlavors.map(function () { return idx; });
    this.resetCartAnimation(); // repaints the bar, and the page row with it
    this.renderPageJars();
  };

  // The "50% off" pill on the button. Only while the button is actually
  // showing that price: one jar, a visitor who can get it, and not once the
  // button has turned into Checkout.
  NovaChatDock.prototype.renderCtaFlag = function () {
    if (!this.el.ctaFlag) return;
    var offering = (this.config.firstOrderPrice || 0) > 0 &&
      this.state.qty === 1 &&
      !this.state.committing &&
      !this.state.revealed;
    this.el.ctaFlag.hidden = !offering;
  };

  NovaChatDock.prototype.idleLabelText = function () {
    // No dash, exactly the app's label: on a 375px phone the dash and its
    // spaces cost ~18px of CTA width the "Edit Jars" chip needs.
    return 'Add to Cart ' + formatMoney(bundlePrice(this.state.qty, this.config));
  };

  NovaChatDock.prototype.renderBuyBar = function () {
    var multiJar = this.state.qty > 1;
    this.el.flavor.hidden = multiJar;
    this.el.jarFlavorRow.hidden = !multiJar;
    if (multiJar) this.renderJarFlavorRow();
    var variant = this.currentVariant();
    if (this.el.flavorName) this.el.flavorName.textContent = variant.title || '';
    if (this.el.qtyValue) this.el.qtyValue.textContent = String(this.state.qty);
    var text = this.idleLabelText();
    if (this.el.ctaSizer) this.el.ctaSizer.textContent = text;
    this.renderCtaFlag();
    if (!this.state.committing && !this.state.revealed) {
      var idle = this.el.cta.querySelector('[data-ncd-cta-label]');
      if (idle) idle.textContent = text;
    }
  };

  /* ---------- add to cart: the chat app's CTA choreography ----------
     Ported from nova-chat-landing.js (addToCart / commitToCart /
     playAddedAnimation / showSettledCta / resetCartAnimation), using the
     same nv-cta-* classes and keyframes from nova-chat-landing.css, which
     the dock snippet loads on the PDP. Timing constant matches too. */

  var ADD_TO_CART_BURST_MS = 700;

  // Why a cart write failed, in words. add_to_cart_failed used to carry
  // only 'form' or the quantity, which is why four sessions of repeated
  // failures in the logs could not be explained after the fact. Shopify
  // answers a rejected cart call with {status, message, description},
  // and the description is the sentence that says what actually went
  // wrong ("You can only add N to the cart", a sold-out line already
  // sitting in the cart, and so on). A fetch that never got a response
  // has none of that, so it reports as a network failure instead, which
  // is itself the answer when an extension or a dead connection is
  // eating the request. Kept well under the /event endpoint's 200-char
  // detail cap.
  function cartFailure(res, body) {
    var why = 'http ' + res.status;
    var said = body && (body.description || body.message);
    if (said) why += ' ' + String(said);
    var err = new Error(why);
    err.novaReason = why;
    return err;
  }

  function cartFailReason(err) {
    if (err && err.novaReason) return String(err.novaReason).slice(0, 150);
    return ('network ' + ((err && err.message) || 'unknown')).slice(0, 150);
  }

  NovaChatDock.prototype.addToCart = function () {
    if (this.state.revealed) {
      // Second click, once the button reads "Checkout": straight to
      // checkout — the item's already in the cart.
      this.track('checkout_clicked', String(this.state.qty));
      window.location.href = this.config.checkoutUrl || '/checkout';
      return;
    }
    this.commitToCart();
  };

  NovaChatDock.prototype.commitToCart = function () {
    if (this.state.committing) return;
    var self = this;
    this.state.committing = true;
    this.playAddedAnimation();
    var synced = this.syncCart();

    setTimeout(function () {
      self.state.committing = false;
      // The settle waits on the cart, not on the timer alone. It used to
      // run unconditionally: the sync's own failure handler would put
      // "Try again" up within ~200ms, then this fired at 700ms and
      // overwrote it with "Checkout" plus the upsell, over a cart that
      // had taken nothing. revealed = true also armed the next tap to
      // jump straight to an empty checkout. That is what four sessions
      // in the logs were doing when they tapped six to eight times in
      // three minutes and never once succeeded.
      synced.then(function (ok) {
        if (!ok) return; // the failure path already owns the button
        self.state.revealed = true;
        self.showSettledCta();
        if (self.state.qty === 1 && !self.state.upsellSeen) self.showUpsell();
      });
    }, ADD_TO_CART_BURST_MS);
  };

  // Alex: the cart should open once jars are added. Runs after the add
  // burst has settled and the cart write has succeeded, so the drawer
  // shows the rebuilt contents (and the first-order discount) instead of
  // cutting the animation off. The guard is released first so a late
  // theme:product:added isn't swallowed; the theme ignores it anyway once
  // the drawer is open. No-op when the theme's cart type isn't a drawer.
  NovaChatDock.prototype.openCartDrawer = function () {
    this.releaseDrawerGuard();
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
  };

  NovaChatDock.prototype.showUpsell = function () {
    this.track('upsell_shown');
    this.el.upsell.classList.add('is-visible');
  };

  NovaChatDock.prototype.hideUpsell = function (seen) {
    this.el.upsell.classList.remove('is-visible');
    if (seen) this.state.upsellSeen = true;
  };

  NovaChatDock.prototype.playAddedAnimation = function () {
    ctaPlayAdded(this.el.cta, this.el.ctaFx, this.idleLabelText(), 'Checkout');
    this.renderCtaFlag(); // committing: the button is no longer an offer
  };

  NovaChatDock.prototype.showSettledCta = function () {
    ctaShowSettled(this.el.cta, 'Checkout');
    this.renderCtaFlag();
  };

  // Back to the idle "Add to Cart" button — any qty/flavor change, or a
  // failed sync. Clears fx layers and every transitional label/ring first
  // so the fresh idle label doesn't stack on a leftover "Checkout".
  NovaChatDock.prototype.resetCartAnimation = function () {
    this.state.committing = false;
    this.state.revealed = false;
    ctaClear(this.el.cta, this.el.ctaFx);
    var idle = document.createElement('span');
    idle.className = 'nv-cta-label nv-cta-label--idle';
    idle.setAttribute('data-ncd-cta-label', '');
    this.el.cta.appendChild(idle);
    this.renderBuyBar();
  };

  function countByVariant(config, jarFlavors) {
    var counts = {};
    jarFlavors.forEach(function (variantIdx) {
      var v = config.variants[variantIdx];
      if (!v || !v.id) return;
      counts[v.id] = (counts[v.id] || 0) + 1;
    });
    return counts;
  }

  // /cart/update.js takes ABSOLUTE quantities, and the cart can already
  // hold jars this widget never wrote: from another page, another tab, the
  // product form, a 3 for $99. Setting a line straight to what this widget
  // wants threw those away: 3 jars in the cart, one tap of the dock's Add
  // to Cart on the homepage, and the cart held 1 (pre-merge review,
  // 2026-09-22). So the cart is read first, and each line keeps whatever
  // it holds beyond what this widget itself last wrote. `committed` is
  // that: {variantId: qty}.
  function keepOthersInCart(cartJsonUrl, updates, committed) {
    return fetch(cartJsonUrl, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart read HTTP ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (item) {
          inCart[item.variant_id] = (inCart[item.variant_id] || 0) + item.quantity;
        });
        var full = {};
        Object.keys(updates).forEach(function (id) {
          full[id] = Math.max(0, (inCart[id] || 0) - (committed[id] || 0)) + updates[id];
        });
        return full;
      });
  }

  var CART_JSON_URL = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';

  // The app's cart sync, verbatim in spirit: makes the real cart match
  // jarFlavors exactly via /cart/update.js (ABSOLUTE quantity per
  // variant — creates, grows, shrinks or removes lines), so a 3-jar
  // order with two flavors lands as two correct lines, and swapping an
  // already-committed jar's flavor drops the old line and raises the new
  // one. Only variants this dock itself committed or now wants appear in
  // the payload, so a line the customer added some other way is never
  // touched. Fire-and-forget behind the burst; failure resets the button.
  NovaChatDock.prototype.syncCart = function () {
    var self = this;
    // Snapshot taken with the payload, not read back when the response
    // lands: the jars can change while the request is in flight.
    var submittedJars = this.state.jarFlavors.slice();
    var writeId = ++this.cartWriteSeq;
    var committed = countByVariant(this.config, this.state.cartJarFlavors || []);
    var target = countByVariant(this.config, this.state.jarFlavors);
    var touched = {};
    Object.keys(committed).forEach(function (id) { touched[id] = true; });
    Object.keys(target).forEach(function (id) { touched[id] = true; });
    var updates = {};
    Object.keys(touched).forEach(function (id) { updates[id] = target[id] || 0; });
    if (!Object.keys(updates).length) return Promise.resolve(true);

    return keepOthersInCart(CART_JSON_URL, updates, committed)
      .then(function (full) {
        return fetch(self.config.cartUpdateUrl || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: full })
        });
      })
      .then(function (res) {
        if (res.ok) return res.json();
        return res.json().catch(function () { return null; }).then(function (body) {
          throw cartFailure(res, body);
        });
      })
      .then(function () {
        if (writeId === self.cartWriteSeq) self.state.cartJarFlavors = submittedJars;
        self.track('add_to_cart_success', String(self.state.qty));
        // Refresh Broadcast's cart count/drawer contents in place, without
        // letting the drawer open yet. Measured live: the theme's own cart
        // rebuild (CartItems.build, run by this refresh) dispatches
        // theme:product:added itself whenever the item count grew, and the
        // drawer opens on that, which cut the add burst short. So that one
        // event is swallowed for the duration of this refresh (see
        // suppressDrawerOpen), and commitToCart opens the drawer itself
        // once the burst has settled (openCartDrawer).
        self.suppressDrawerOpen();
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        return true;
      })
      .catch(function (err) {
        self.track('add_to_cart_failed', String(self.state.qty) + ' | ' + cartFailReason(err));
        self.resetCartAnimation();
        var idle = self.el.cta.querySelector('[data-ncd-cta-label]');
        if (idle) idle.textContent = 'Try again';
        setTimeout(function () { self.renderBuyBar(); }, 1600);
        return false;
      });
  };

  // Capture-phase listener on document: the theme dispatches
  // theme:product:added ON document, so at-target capture listeners run
  // before its own (bubble-registered) drawer opener, and
  // stopImmediatePropagation keeps the drawer shut. One-shot: released on
  // the first such event, or after a timeout if the rebuild never fires
  // it (count unchanged), so a later real add from the product form still
  // opens the drawer as normal.
  NovaChatDock.prototype.suppressDrawerOpen = function () {
    var self = this;
    this.releaseDrawerGuard();
    this._drawerGuard = function (e) {
      e.stopImmediatePropagation();
      self.releaseDrawerGuard();
    };
    document.addEventListener('theme:product:added', this._drawerGuard, true);
    this._drawerGuardTimer = setTimeout(function () { self.releaseDrawerGuard(); }, 4000);
  };

  NovaChatDock.prototype.releaseDrawerGuard = function () {
    clearTimeout(this._drawerGuardTimer);
    if (!this._drawerGuard) return;
    document.removeEventListener('theme:product:added', this._drawerGuard, true);
    this._drawerGuard = null;
  };

  /* ---------- the product form's own Add to Cart ----------
     Same look, choreography and states as the dock's button, on
     Broadcast's on-page button (it was inverted to black glass for the
     light page; nova-chat-dock.css .ncd-main-cta has the story). The
     button keeps its theme typography; only overlay layers and absolute
     labels are added.

     Broadcast's <product-form> binds `submit` on its form and dispatches
     its own AJAX add (theme:cart:add -> drawer opens) from there. A
     capture-phase submit listener on document runs first and stops it,
     so the add goes through the same /cart/update.js sync as the dock
     (absolute qty; the drawer opens after the burst, see openCartDrawer).
     The form stays the source of truth for
     variant (hidden [name=id], kept current by the theme) and quantity
     (the offer cards write it). No upsell under this button: the cards
     above it already offer the three jars (Alex, 2026-09-22). */

  function NovaMainCta(dock) {
    this.dock = dock;
    this.config = dock.config;
    this.button = document.querySelector('.product__page [data-add-to-cart]');
    this.form = this.button && (this.button.form || this.button.closest('form'));
    if (!this.button || !this.form) return;
    this.textEl = this.button.querySelector('[data-add-to-cart-text]') || this.button;
    this.state = { committing: false, revealed: false, committed: null };
    this.setup();
  }

  NovaMainCta.prototype.setup = function () {
    // The button BECOMES the app's CTA (same classes as the dock's), not a
    // theme .btn with layers on top: Broadcast's .btn cascade (font,
    // uppercase, background transition, `.btn span` positioning, loader/
    // added overlays) fought every beat of the choreography. Its theme
    // hooks stay: type=submit, data-add-to-cart, [data-add-to-cart-text]
    // (which the theme rewrites to "Sold out" and disables on variant
    // change), so sold-out handling is untouched.
    this.button.className = 'nv-cta ncd-main-cta';
    this.fx = document.createElement('span');
    this.fx.className = 'nv-cta-fx';
    this.button.insertBefore(this.fx, this.button.firstChild);

    // The visible idle label is ours (absolute, like the app's), not the
    // theme's text span: Broadcast rewrites that span on every variant
    // change ("Add to cart" / "Sold out"), and letting it show even for a
    // frame flicked the label between "Add to Cart" and "Add to Cart $49".
    // The span stays in flow, invisible, as the sizer and as the theme's
    // channel for sold-out text; a MutationObserver re-renders our label
    // from it before paint whenever the theme touches it.
    var self = this;
    this.idleEl = document.createElement('span');
    this.idleEl.className = 'nv-cta-label nv-cta-label--idle';
    this.idleEl.setAttribute('data-ncd-main-idle', '');
    this.button.appendChild(this.idleEl);
    this.renderIdle();
    new MutationObserver(function () { self.renderIdle(); })
      .observe(this.textEl, { childList: true, characterData: true, subtree: true });

    this.watchVariantPicker();

    // Matched by "the form our button is in", not by identity with the node
    // captured at setup: the theme re-renders parts of the buy block on
    // variant and cart updates, and a stale reference here would silently
    // hand the submit back to Broadcast's own add (or drop it).
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || typeof form.contains !== 'function') return;
      if (!form.contains(self.button)) return;
      self.form = form;
      e.preventDefault();
      e.stopImmediatePropagation();
      self.onSubmit();
    }, true);

    // Any variant or quantity change returns the button to Add to Cart,
    // as in the app. The theme's own variant-change subscriber (registered
    // earlier) has already rewritten the label/disabled state by the time
    // ours runs, so renderIdle() sees the final state.
    this.form.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'quantity') self.reset();
    });
    if (typeof window.subscribe === 'function' && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (e) {
        self.reset();
        // The dock owns the per-jar flavors for this form too: a flavor pick
        // on the page sets every jar to it, on both surfaces.
        var variant = e && e.data && e.data.variant;
        if (variant) self.dock.setAllJarsToVariant(variant.id);
      });
    }
  };

  // Idle label = plain "Add to Cart" when the offer cards are on the page
  // (snippets/product-offer-tiers.liquid), since the selected card right
  // above carries the price; otherwise the app's "Add to Cart $49". Or
  // whatever the theme put in its span when the chosen variant is genuinely
  // unavailable ("Sold out"). Availability is
  // read from the variant data, NOT from button.disabled: Broadcast
  // disables the button for a beat during every variant switch, and
  // keying on that mirrored its "Add to cart" for a frame before ours
  // came back — the flick. Re-attaches the label if the choreography
  // helpers removed it.
  NovaMainCta.prototype.renderIdle = function () {
    if (this.state.committing || this.state.revealed) return;
    if (!this.idleEl.isConnected) this.button.appendChild(this.idleEl);
    var order = this.readForm();
    var variant = (this.config.variants || []).filter(function (v) { return String(v.id) === String(order.id); })[0];
    var unavailable = variant ? !variant.available : this.button.disabled;
    this.idleEl.textContent = unavailable
      ? this.textEl.textContent.trim()
      : document.querySelector('[data-nfo-tiers]')
        ? 'Add to Cart'
        : 'Add to Cart ' + formatMoney(bundlePrice(order.qty, this.config));
  };

  // Flavor picker. Measured on the page: clicking a flavor checks the
  // radio immediately, and Broadcast then replaces the whole buy block —
  // 24 elements, the picker and the flavour readout among them — about
  // 335ms later. So every animation here runs on the LIVE nodes the
  // instant the tap lands and is short enough to finish before that
  // swap; the fresh markup then renders at the state the animation
  // reached, making the swap invisible. Nothing is animated afterwards
  // (that replayed the whole entrance, which is what read as a flick),
  // and nothing is hung off the fieldset, which does not survive it.
  NovaMainCta.prototype.watchVariantPicker = function () {
    var self = this;
    var checked = document.querySelector('.product__page .radio__fieldset input[type="radio"]:checked');
    this.currentValue = checked ? checked.value : null;

    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      if (!input.closest || !input.closest('.product__page .radio__fieldset')) return;
      var fieldset = input.closest('.radio__fieldset');
      var previous = self.currentValue;
      self.currentValue = input.value;

      // The tapped pill gets the settle pop. The fills themselves are the
      // :checked transitions in the CSS, not keyframes: a keyframe with
      // fill-mode `both` holds its end state, and if the block was never
      // replaced (a slow or failed request) a pill could keep an opacity
      // that no longer matched whether it was checked — one of them stuck
      // unfilled. Clearing the class off every label first also means a
      // fast back-and-forth can't leave a stale one behind.
      fieldset.querySelectorAll('label.ncd-picked').forEach(function (l) {
        l.classList.remove('ncd-picked');
      });
      var label = input.parentElement && input.parentElement.querySelector('label');
      if (label) {
        void label.offsetWidth; // restart the pop if the same pill is re-picked
        label.classList.add('ncd-picked');
      }

      self.wipeFlavorName(fieldset, previous, input.value);
    }, true);

    // The buy block is replaced mid-crossfade; this puts the crossfade
    // back on the new readout at the frame it had reached, so the swap is
    // invisible instead of cutting the words off. Measured: the outgoing
    // and incoming blocks are both in the document for a frame or two, so
    // two readouts — four words — can render side by side (Alex: "it
    // triples the words into a really long line"). Only the copy actually
    // carrying the crossfade is left visible; endNameSwap always restores
    // the rest.
    var root = document.querySelector('.product__page');
    if (!root) return;
    new MutationObserver(function () {
      var swap = self.nameSwap;
      if (!swap) return;
      if (performance.now() - swap.at >= NAME_SWAP_MS) { self.endNameSwap(); return; }
      var readouts = self.flavorReadouts();
      if (!readouts.length) return;
      var fresh = null;
      for (var i = 0; i < readouts.length; i++) {
        if (!readouts[i].querySelector('.ncd-flavor-swap')) { fresh = readouts[i]; break; }
      }
      if (fresh) {
        self.buildNameSwap(fresh, swap.from, swap.to, performance.now() - swap.at);
      }
      if (self.activeReadout && self.activeReadout.isConnected) {
        readouts.forEach(function (r) {
          if (r !== self.activeReadout) r.style.visibility = 'hidden';
        });
      }
    }).observe(root, { childList: true, subtree: true });
  };

  NovaMainCta.prototype.flavorReadouts = function () {
    return Array.prototype.slice.call(
      document.querySelectorAll('.product__page .radio__fieldset [data-selected-value]')
    );
  };

  // Always reachable: clears the crossfade state, un-hides every readout
  // so a mis-identified copy can never stay hidden, and settles the
  // finished readout back to plain text — the outgoing span ends
  // invisible but would otherwise linger, doubling the readable text.
  NovaMainCta.prototype.endNameSwap = function () {
    var swap = this.nameSwap;
    var active = this.activeReadout;
    this.nameSwap = null;
    this.activeReadout = null;
    clearTimeout(this._nameSwapTimer);
    this.flavorReadouts().forEach(function (r) { r.style.visibility = ''; });
    if (swap && active && active.isConnected && active.querySelector('.ncd-flavor-swap')) {
      active.textContent = swap.to;
    }
  };

  // The flavour readout above the pills ("Pina Colada"): the old name
  // wipes off to the right while the new one wipes on behind it, both
  // left to right — the same clip-path crossfade the chat app's CTA uses
  // for its label. The new name sits in flow so the line keeps its final
  // width; the old one is absolute on top of it.
  NovaMainCta.prototype.wipeFlavorName = function (fieldset, previous, next) {
    if (!fieldset || previous === null || previous === next) return;
    var readout = fieldset.querySelector('[data-selected-value]');
    if (!readout) return;
    var self = this;
    // The "Flavor:" tag settles under the wipe (secondary layer; the
    // class and its keyframes are in nova-chat-dock.css). Remove, reflow,
    // re-add so a fast second pick restarts it.
    var label = fieldset.querySelector('.radio__legend__label');
    if (label) {
      label.classList.remove('ncd-label-pop');
      void label.offsetWidth;
      label.classList.add('ncd-label-pop');
    }
    this.endNameSwap(); // a pick landing mid-crossfade restarts cleanly
    // The option's own value, not the readout's text: mid-crossfade that
    // text is both names at once, which would put "Pina ColadaMango
    // Cream" into the outgoing span on a fast double-tap.
    var oldText = previous || (readout.textContent || '').trim();
    this.nameSwap = { from: oldText, to: next, at: performance.now() };
    this.buildNameSwap(readout, oldText, next, 0);
    this._nameSwapTimer = setTimeout(function () { self.endNameSwap(); }, NAME_SWAP_MS + 150);
  };

  // `elapsed` > 0 resumes a crossfade that started before the block was
  // replaced: the spans are rebuilt and their animations scrubbed to
  // where they had got to.
  NovaMainCta.prototype.buildNameSwap = function (readout, from, to, elapsed) {
    readout.textContent = '';
    var swap = document.createElement('span');
    swap.className = 'ncd-flavor-swap';
    var out = document.createElement('span');
    out.className = 'ncd-flavor-swap__old';
    out.textContent = from;
    var incoming = document.createElement('span');
    incoming.className = 'ncd-flavor-swap__new';
    incoming.textContent = to;
    swap.appendChild(out);
    swap.appendChild(incoming);
    readout.appendChild(swap);
    readout.style.visibility = '';
    this.activeReadout = readout;
    if (elapsed > 0) {
      [out, incoming].forEach(function (el) {
        el.getAnimations().forEach(function (a) {
          try { a.currentTime = elapsed; } catch (e) { /* older engines */ }
        });
      });
    }
  };

  NovaMainCta.prototype.readForm = function () {
    var idInput = this.form.querySelector('[name="id"]');
    var qtyInput = this.form.querySelector('[name="quantity"]');
    return {
      id: idInput ? idInput.value : null,
      qty: Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1)
    };
  };

  NovaMainCta.prototype.onSubmit = function () {
    if (this.button.disabled) return;
    if (this.state.revealed) {
      this.dock.track('checkout_clicked', String(this.readForm().qty));
      window.location.href = this.config.checkoutUrl || '/checkout';
      return;
    }
    this.commit();
  };

  NovaMainCta.prototype.commit = function () {
    if (this.state.committing) return;
    var self = this;
    var order = this.readForm();
    if (!order.id) return;
    this.state.committing = true;
    ctaPlayAdded(this.button, this.fx, this.idleEl.textContent, 'Checkout'); // removes the idle label with the others
    var synced = this.sync(order);
    setTimeout(function () {
      self.state.committing = false;
      // Same gate as the dock's buy bar: claim the add only once the cart
      // has actually taken it, or a failed write still reads as
      // "Checkout" over an empty cart.
      synced.then(function (ok) {
        if (!ok) return; // the failure path already owns the button
        self.state.revealed = true;
        ctaShowSettled(self.button, 'Checkout');
        self.dock.openCartDrawer();
        // The drawer covers this button while it is open, so its taps land
        // on the drawer's own overlay and the button reads as dead
        // ("pressed Checkout, literally nothing happened" — Alex). The
        // drawer owns checkout from here; when it closes, the button goes
        // back to Add to Cart rather than sitting on a Checkout state the
        // visitor already scrolled past.
        var restore = function () {
          document.removeEventListener('theme:cart-drawer:close', restore);
          self.reset();
        };
        document.addEventListener('theme:cart-drawer:close', restore);
      });
    }, ADD_TO_CART_BURST_MS);
  };

  // What this form's order puts in the cart, as { variantId: qty }. One
  // line normally; with the offer tiers and more than one jar, one line per
  // flavor from the dock's per-jar picks (set with "Edit jars").
  NovaMainCta.prototype.orderCounts = function (order) {
    var dock = this.dock;
    if (order.qty > 1 && dock.state.pageJars.length === order.qty) {
      return countByVariant(dock.config, dock.state.pageJars);
    }
    var counts = {};
    counts[order.id] = order.qty;
    return counts;
  };

  NovaMainCta.prototype.sync = function (order) {
    var self = this;
    var dock = this.dock;
    var counts = this.orderCounts(order);
    // The counts belong to THIS payload. Reading them back when the
    // response lands let a slow write record a selection the visitor had
    // already changed: submit 2 A + 1 B, switch to 3 B while it is in
    // flight, and the older response would claim 3 B was in the cart, so
    // the next add left the two A jars behind and the cart held five.
    var writeId = ++dock.cartWriteSeq;
    // This form's own order, in absolute quantities: every line it
    // committed last time and no longer wants goes back to 0, so a re-add
    // after a change replaces its earlier order instead of stacking on it.
    // Anything else in the cart is kept (keepOthersInCart).
    var updates = {};
    var previous = this.state.committed || {};
    Object.keys(previous).forEach(function (id) { if (!(id in counts)) updates[id] = 0; });
    Object.keys(counts).forEach(function (id) { updates[id] = counts[id]; });
    return keepOthersInCart(CART_JSON_URL, updates, previous)
      .then(function (full) {
        return fetch(self.config.cartUpdateUrl || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: full })
        });
      })
      .then(function (res) {
        if (res.ok) return res.json();
        return res.json().catch(function () { return null; }).then(function (body) {
          throw cartFailure(res, body);
        });
      })
      .then(function () {
        // The dock no longer takes this form's jars as its own: with the
        // cart read first, it keeps them as someone else's.
        if (writeId === dock.cartWriteSeq) self.state.committed = counts;
        dock.track('add_to_cart_success', 'form x' + order.qty);
        self.dock.suppressDrawerOpen();
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        return true;
      })
      .catch(function (err) {
        self.dock.track('add_to_cart_failed', 'form | ' + cartFailReason(err));
        self.reset();
        self.idleEl.textContent = 'Try again';
        setTimeout(function () { self.renderIdle(); }, 1600);
        return false;
      });
  };

  NovaMainCta.prototype.reset = function () {
    this.state.committing = false;
    this.state.revealed = false;
    ctaClear(this.button, this.fx);
    this.renderIdle();
  };

  /* ---------- funnel beacon (same contract as nova-chat-landing.js) ---------- */

  NovaChatDock.prototype.track = function (name, detail) {
    var endpoint = this.config.chatEndpoint;
    if (!endpoint) return;
    try {
      var url = endpoint.replace(/\/chat(?=$|\?)/, '/event');
      if (url === endpoint) return;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.sessionId,
          name: name,
          detail: detail == null || detail === '' ? undefined : String(detail).slice(0, 200)
        }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never let tracking break the dock */ }
  };

  customElements.define('nova-chat-dock', NovaChatDock);
})();
